<template>
    <div class="report_entry_wrap" v-if="buttonVisible">
        <!-- 可拖动悬浮按钮（避开微信右上角占位） -->
        <div
            class="report_float_btn"
            :style="floatBtnStyle"
            @touchstart="onTouchStart"
            @touchmove.prevent="onTouchMove"
            @touchend="onTouchEnd"
            @touchcancel="onTouchEnd"
            @mousedown="onMouseDown">
            <span class="report_float_btn_text">报备</span>
            <div class="report_float_badge" v-if="unreadCount > 0">
                <span class="report_float_badge_text">{{ badgeText }}</span>
            </div>
        </div>
        <van-popup
            v-model="show"
            round
            position="bottom"
            :close-on-click-overlay="false"
            :safe-area-inset-bottom="true"
            get-container="body">
            <order-report
                ref="reportPanel"
                v-if="show"
                :orderId="orderId"
                :orderInfo="orderInfo"
                @close="close"
                @badgeRefresh="onBadgeRefresh" />
        </van-popup>
    </div>
</template>

<script>
import orderReport from '@/components/orderReport/orderReport.vue';
import { getReport } from '@/apis/checkList/report.js';
import { isShowReportBtn, buildReportSummary } from '@/utils/orderReport.js';
import { normalizeReportList } from '@/utils/reportFile.js';
import { Toast } from 'vant';

const POLL_INTERVAL = 15000;
const POLL_INTERVAL_POPUP = 8000;
const BTN_SIZE = 40;
const BTN_MARGIN = 10;

export default {
    name: 'orderReportEntry',
    components: {
        orderReport
    },
    props: {
        orderId: {
            type: [String, Number],
            default: ''
        },
        orderInfo: {
            type: Object,
            default: null
        },
        scene: {
            type: String,
            default: 'driver'
        }
    },
    data() {
        return {
            show: false,
            unreadCount: 0,
            pollTimer: null,
            lastFingerprint: '',
            hasPending: false,
            btnTop: 0,
            dragging: false,
            moved: false,
            startY: 0,
            startTop: 0,
            touchMode: false
        };
    },
    computed: {
        buttonVisible() {
            return isShowReportBtn(this.orderInfo, this.scene);
        },
        badgeText() {
            return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
        },
        floatBtnStyle() {
            return {
                top: `${this.btnTop}px`,
                right: `${BTN_MARGIN}px`
            };
        }
    },
    watch: {
        orderInfo: {
            handler() {
                this.refreshBadgeState();
            },
            deep: true
        },
        orderId() {
            this.lastFingerprint = '';
            this.refreshBadgeState();
        },
        buttonVisible(val) {
            if (val) {
                this.refreshBadgeState();
            } else {
                this.unreadCount = 0;
                this.hasPending = false;
                this.stopPolling();
            }
        },
        show() {
            this.restartPollingIfNeeded();
        }
    },
    mounted() {
        this.initBtnPosition();
        this.refreshBadgeState();
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('resize', this.onResize);
    },
    beforeDestroy() {
        this.stopPolling();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        window.removeEventListener('resize', this.onResize);
        this.unbindMouse();
    },
    methods: {
        initBtnPosition() {
            const vh = window.innerHeight || 667;
            // 默认放在右侧中偏下，避开微信右上角菜单
            this.btnTop = Math.min(Math.max(vh * 0.45, 120), vh - BTN_SIZE - 100);
        },
        onResize() {
            this.clampBtnTop();
        },
        clampBtnTop() {
            const vh = window.innerHeight || 667;
            const minTop = BTN_MARGIN + 80; // 避开顶部微信胶囊区
            const maxTop = vh - BTN_SIZE - BTN_MARGIN - 80; // 避开底部操作栏
            this.btnTop = Math.min(Math.max(this.btnTop, minTop), Math.max(minTop, maxTop));
        },
        getOrderId() {
            return this.orderId || (this.orderInfo && this.orderInfo.id) || '';
        },
        getPollInterval() {
            return this.show ? POLL_INTERVAL_POPUP : POLL_INTERVAL;
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.refreshBadgeState();
            } else {
                this.stopPolling();
            }
        },
        notifyReportPanel(payload) {
            if (!this.show || !this.$refs.reportPanel) {
                return;
            }
            this.$refs.reportPanel.refreshFromPush(payload);
        },
        // 面板已拉过列表时直接更新角标，避免重复请求
        onBadgeRefresh(list) {
            if (Array.isArray(list)) {
                const summary = buildReportSummary(list);
                this.applySummary(list, summary, { source: 'panel' });
                return;
            }
            this.fetchReportBadge();
        },
        refreshBadgeState() {
            if (!this.buttonVisible) {
                this.unreadCount = 0;
                this.hasPending = false;
                this.stopPolling();
                return;
            }
            this.fetchReportBadge();
        },
        applySummary(list, summary, options = {}) {
            const { forcePanelRefresh = false, source = 'poll', pushPayload = null } = options;
            this.unreadCount = summary.unread;
            this.hasPending = summary.has_pending;
            const fingerprintChanged = summary.fingerprint !== this.lastFingerprint;
            if (fingerprintChanged) {
                this.lastFingerprint = summary.fingerprint;
            }
            // 来自面板自身列表时不再回写，避免二次刷新
            if (source !== 'panel' && this.show && (forcePanelRefresh || fingerprintChanged)) {
                this.notifyReportPanel({
                    source,
                    list,
                    report_id: pushPayload && pushPayload.report_id,
                    type: pushPayload && pushPayload.type
                });
            }
            if (summary.has_pending && document.visibilityState === 'visible') {
                this.startPolling();
            } else {
                this.stopPolling();
            }
        },
        async fetchReportBadge(forcePanelRefresh = false, pushPayload = null) {
            const orderId = this.getOrderId();
            if (!orderId || !this.buttonVisible) {
                this.unreadCount = 0;
                this.hasPending = false;
                this.stopPolling();
                return;
            }
            try {
                const res = await getReport({ order_id: orderId });
                const list = normalizeReportList(res);
                const summary = buildReportSummary(list);
                this.applySummary(list, summary, {
                    forcePanelRefresh,
                    source: forcePanelRefresh ? 'push' : 'poll',
                    pushPayload
                });
            } catch (err) {
                console.log('[reportEntry] fetchReportBadge error', err);
            }
        },
        startPolling() {
            if (this.pollTimer || document.visibilityState !== 'visible') {
                return;
            }
            const interval = this.getPollInterval();
            this.pollTimer = setInterval(() => {
                this.fetchReportBadge();
            }, interval);
        },
        restartPollingIfNeeded() {
            if (!this.hasPending || document.visibilityState !== 'visible') {
                return;
            }
            this.stopPolling();
            this.startPolling();
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        onTouchStart(e) {
            const touch = e.touches[0];
            if (!touch) {
                return;
            }
            this.touchMode = true;
            this.dragging = true;
            this.moved = false;
            this.startY = touch.clientY;
            this.startTop = this.btnTop;
        },
        onTouchMove(e) {
            if (!this.dragging) {
                return;
            }
            const touch = e.touches[0];
            if (!touch) {
                return;
            }
            const deltaY = touch.clientY - this.startY;
            if (Math.abs(deltaY) > 6) {
                this.moved = true;
            }
            if (this.moved) {
                this.btnTop = this.startTop + deltaY;
                this.clampBtnTop();
            }
        },
        onTouchEnd() {
            if (!this.dragging) {
                return;
            }
            this.dragging = false;
            this.clampBtnTop();
            // touchstart 上 preventDefault 会吞掉 click，改为松手时打开
            if (!this.moved) {
                this.open();
            }
            this.moved = false;
            // 避免紧随其后的合成 mouse 事件再次触发
            setTimeout(() => {
                this.touchMode = false;
            }, 350);
        },
        onMouseDown(e) {
            // 触控设备会先触发 touch，再合成 mouse，避免重复打开
            if (this.touchMode) {
                return;
            }
            e.preventDefault();
            this.dragging = true;
            this.moved = false;
            this.startY = e.clientY;
            this.startTop = this.btnTop;
            document.addEventListener('mousemove', this.onMouseMove);
            document.addEventListener('mouseup', this.onMouseUp);
        },
        onMouseMove(e) {
            if (!this.dragging) {
                return;
            }
            const deltaY = e.clientY - this.startY;
            if (Math.abs(deltaY) > 6) {
                this.moved = true;
            }
            if (this.moved) {
                this.btnTop = this.startTop + deltaY;
                this.clampBtnTop();
            }
        },
        onMouseUp() {
            if (!this.dragging) {
                this.unbindMouse();
                return;
            }
            this.dragging = false;
            this.unbindMouse();
            this.clampBtnTop();
            if (!this.moved) {
                this.open();
            }
            this.moved = false;
        },
        unbindMouse() {
            document.removeEventListener('mousemove', this.onMouseMove);
            document.removeEventListener('mouseup', this.onMouseUp);
        },
        open() {
            if (!this.orderInfo) {
                Toast('订单信息加载中');
                return;
            }
            this.show = true;
        },
        close() {
            this.show = false;
            this.fetchReportBadge();
        }
    }
};
</script>

<style lang="scss" scoped>
.report_entry_wrap {
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    overflow: visible;
}
.report_float_btn {
    position: fixed;
    width: 40px;
    height: 40px;
    border-radius: 20px;
    background: linear-gradient(135deg, #357EFE 0%, #5B9BFF 100%);
    box-shadow: 0 3px 10px rgba(53, 126, 254, 0.35);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
}
.report_float_btn_text {
    font-size: 12px;
    color: #ffffff;
    font-weight: 600;
    letter-spacing: 0;
}
.report_float_badge {
    position: absolute;
    top: -3px;
    right: -3px;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    border-radius: 7px;
    background: #F56C6C;
    border: 1px solid #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
}
.report_float_badge_text {
    font-size: 9px;
    color: #ffffff;
    line-height: 12px;
    font-weight: bold;
}
</style>
